import React from "react";
import { TagInputProps } from "../../types/tag-input-types";
import { useTagInput } from "../../hooks/useTagInput";

const TagInput: React.FC<TagInputProps> = ({
  value = [],
  onChange,
  placeholder = "Add a tag...",
  label,
  disabled = false,
  error,
  helperText,
  containerStyle = {},
  labelStyle = {},
  inputStyle = {},
  tagStyle = {},
  className = "",
  onBlur,
  infoText = "Press Enter to add",
}) => {
  const {
    inputValue,
    isFocused,
    setIsFocused,
    inputRef,
    handleInputChange,
    handleKeyDown,
    removeTag,
    handleContainerClick,
  } = useTagInput({ value, onChange, disabled });

  const defaultContainerStyle: React.CSSProperties = {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "8px",
    padding: "8px 12px",
    border: error
      ? "1px solid #ef4444"
      : isFocused
      ? "1px solid #3b82f6"
      : "1px solid #d1d5db",
    borderRadius: "8px",
    backgroundColor: disabled ? "#f3f4f6" : "#ffffff",
    cursor: disabled ? "not-allowed" : "text",
    minHeight: "42px",
    transition: "all 0.2s ease",
    boxShadow:
      isFocused && !error ? "0 0 0 3px rgba(59, 130, 246, 0.1)" : "none",
    ...containerStyle,
  };

  const defaultTagStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    backgroundColor: "#f3f4f6",
    padding: "4px 8px",
    borderRadius: "6px",
    fontSize: "14px",
    color: "#374151",
    ...tagStyle,
  };

  return (
    <div className={className} style={{ width: "100%", marginBottom: "16px" }}>
      {label && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "8px",
          }}
        >
          <label
            style={{
              fontSize: "14px",
              fontWeight: "500",
              color: "#374151",
              ...labelStyle,
            }}
          >
            {label}
          </label>
          {infoText && (
            <span style={{ fontSize: "12px", color: "#6b7280" }}>
              {infoText}
            </span>
          )}
        </div>
      )}

      <div style={defaultContainerStyle} onClick={handleContainerClick}>
        {value.map((tag, index) => (
          <div key={index} style={defaultTagStyle}>
            <span style={{ marginRight: "6px" }}>{tag}</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeTag(index);
              }}
              disabled={disabled}
              style={{
                border: "none",
                background: "transparent",
                cursor: disabled ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#6b7280",
                padding: 0,
                fontSize: "16px",
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>
        ))}

        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setIsFocused(false);
            onBlur?.();
          }}
          placeholder={value.length === 0 ? placeholder : ""}
          disabled={disabled}
          style={{
            border: "none",
            outline: "none",
            fontSize: "14px",
            flex: 1,
            minWidth: "120px",
            backgroundColor: "transparent",
            padding: 0,
            color: "#1f2937",
            ...inputStyle,
          }}
        />
      </div>

      {error ? (
        <p
          style={{
            marginTop: "4px",
            fontSize: "12px",
            color: "#ef4444",
            margin: 0,
          }}
        >
          {error}
        </p>
      ) : helperText ? (
        <p
          style={{
            marginTop: "4px",
            fontSize: "12px",
            color: "#6b7280",
            margin: 0,
          }}
        >
          {helperText}
        </p>
      ) : null}
    </div>
  );
};

export default TagInput;
